/**
 * StatsSection, Sprint 2.5a A.0/B/S7c.
 *
 * Replaces the prior StatsAndCloser's stats half AND the standalone
 * BeliefBelt section. Both were doing complementary work, BeliefBelt
 * was the lyrical frame ("Australians say they want to be seen.
 * Just not to be put on stage."), stats were the evidence, but
 * they sat in different parts of the page and felt like separate
 * beats. Lucky's call: have them coexist as one editorial section.
 *
 * Structure (top to bottom):
 *   1. Coral leading rule (BeliefBelt's signature motif, kept)
 *   2. Italic Newsreader headline (BeliefBelt's wisdom line)
 *   3. DM Sans subordinate line (BeliefBelt's hypothesis statement)
 *   4. Three stat cards (white / butter / white) with animated
 *      count-up tickers (S5's StatsAndCloser cards, kept)
 *   5. Sources line under each card (Reward Gateway / Gallup / Aberdeen)
 *
 * No closing CTA, that's now its own <FinalCTA /> section directly
 * after, with a distinct navy treatment (bookends HowLembraWorks).
 *
 * Pioneer alignment kept intact:
 * - Norman: count-up animation signals "live data, look here"
 * - Stanley: aria-live on each numeral; once-only IntersectionObserver
 *   trigger; reduced-motion respected
 */

import { useEffect, useRef, useState } from "react";
import { Eye, TrendingUp, Users, type LucideIcon } from "lucide-react";

import { useReducedMotion } from "@/hooks/use-reduced-motion";

const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const displayFamily = "Newsreader, Georgia, serif";
const sansFamily = "DM Sans, sans-serif";

interface StatCard {
  title: string;
  value: number;
  suffix: string;
  thousands?: boolean;
  descriptor: string;
  source: string;
  /** MS24: per-card lucide icon as a quiet visual signifier, makes
   *  each card recognisable without reading the title. */
  icon: LucideIcon;
  /** MS24: each card now picks its own subtle accent gradient
   *  background so the row reads as three distinct moments instead
   *  of three identical white-walled boxes. */
  accent: "butter" | "coral" | "sky";
}

const STATS: StatCard[] = [
  {
    title: "Three in four feel unseen at work.",
    value: 23,
    suffix: "%",
    descriptor:
      "of Australian employees feel appreciated, down from 38% the year before.",
    source: "Reward Gateway AU · Workplace Engagement Index 2025",
    icon: Eye,
    accent: "coral",
  },
  {
    title: "Recognition multiplies engagement.",
    value: 9,
    suffix: "×",
    descriptor:
      "more engaged when team milestones get reliably recognised.",
    source: "Gallup · Employee Retention & Recognition Study",
    icon: TrendingUp,
    accent: "butter",
  },
  {
    title: "Recognition retains people.",
    value: 31,
    suffix: "%",
    descriptor:
      "lower voluntary turnover at companies that mark milestones consistently.",
    source: "Aberdeen Group · Human Capital Management",
    icon: Users,
    accent: "sky",
  },
];

/* MS58 — minimal cards. Gradients removed (Lucky's note: "remove
   the gradient colors as well, keep it a nice solid color").
   Body is one flat near-white; the only chroma left is the top
   accent rule (per-card punctuation) and the icon glyph itself.
   The icon chip background is dropped — the rule already
   differentiates the cards, the chip was redundant tint. */
const CARD_BG = "#FFFFFF";
const ACCENT_ICON_FG: Record<StatCard["accent"], string> = {
  coral: "#C4564A",
  butter: "#B8932A", // a touch deeper than the rule so the glyph reads
  sky: "#1D3461",
};
const ACCENT_RULE: Record<StatCard["accent"], string> = {
  coral: "#C4564A",
  butter: "#E8C758",
  sky: "#7BA3CC",
};

function AnimatedNumeral({
  value,
  suffix,
  thousands,
  durationMs = 1400,
}: {
  value: number;
  suffix: string;
  thousands?: boolean;
  durationMs?: number;
}) {
  const [displayed, setDisplayed] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);
  // MS184: shared useReducedMotion hook replaces the inline
  // matchMedia check. Reactive — re-rendering when the OS pref
  // toggles. The effect's closure still snapshots the value on
  // animation start (it's a one-shot count-up that only fires
  // when the element scrolls into view), so a live toggle won't
  // retroactively change the running animation — same behaviour
  // as before, just less duplication.
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;
        if (reducedMotion) {
          setDisplayed(value);
          return;
        }
        const start = performance.now();
        let frame: number;
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / durationMs);
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplayed(Math.round(value * eased));
          if (t < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, durationMs, reducedMotion]);

  const formatted = thousands
    ? displayed.toLocaleString("en-AU").replace(/,/g, " ")
    : String(displayed);

  return (
    <span
      ref={ref}
      aria-live="polite"
      aria-atomic="true"
      style={{
        fontFamily: displayFamily,
        fontStyle: "italic",
        fontWeight: 500,
        fontSize: "clamp(3rem, 5.5vw, 4.6rem)",
        lineHeight: 1,
        letterSpacing: "-0.025em",
        color: "#1D3461",
        display: "inline-block",
      }}
    >
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {formatted}
        <span style={{ fontStyle: "normal", marginLeft: "0.05em" }}>{suffix}</span>
      </span>
    </span>
  );
}

function StatCardView({ card }: { card: StatCard }) {
  const Icon = card.icon;
  return (
    <div
      className="flex h-full flex-col rounded-2xl px-6 py-7 md:px-7 md:py-8 relative overflow-hidden"
      style={{
        // MS58: solid near-white background (gradient removed). Soft
        // shadow stripped too — the hairline navy/8 border alone
        // gives the card a quiet boundary without sitting "on top
        // of" the section. Reads as flat editorial card, not a tile.
        background: CARD_BG,
        border: "1px solid rgba(29,52,97,0.08)",
      }}
    >
      {/* Per-accent leading rule at the top of each card. Last bit
          of chroma the card carries — gives each its own visual
          punctuation without competing with the numeral. */}
      <div
        aria-hidden="true"
        style={{
          width: 32,
          height: 2,
          background: ACCENT_RULE[card.accent],
          borderRadius: 999,
          marginBottom: 18,
        }}
      />

      {/* MS58: icon chip background removed (was a soft accent tint).
          The glyph now stands alone in its accent colour at the same
          18px size, which reads as quieter and matches Aesop-style
          editorial restraint. */}
      <div aria-hidden="true" className="mb-5 inline-flex">
        <Icon size={18} strokeWidth={1.8} style={{ color: ACCENT_ICON_FG[card.accent] }} />
      </div>

      <p
        style={{
          fontFamily: displayFamily,
          fontWeight: 500,
          fontSize: "1.05rem",
          lineHeight: 1.3,
          letterSpacing: "-0.005em",
          color: "#1D3461",
          margin: 0,
          maxWidth: "26ch",
        }}
      >
        {card.title}
      </p>

      <div className="mt-auto pt-8">
        <AnimatedNumeral
          value={card.value}
          suffix={card.suffix}
          thousands={card.thousands}
        />
        <p
          style={{
            fontFamily: sansFamily,
            fontSize: "0.875rem",
            lineHeight: 1.55,
            color: "rgba(29,52,97,0.72)",
            margin: "12px 0 0",
          }}
        >
          {card.descriptor}
        </p>
        <p
          style={{
            fontFamily: monoFamily,
            fontSize: "0.62rem",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "rgba(29,52,97,0.42)",
            margin: "14px 0 0",
          }}
        >
          {card.source}
        </p>
      </div>
    </div>
  );
}

interface StatsSectionProps {
  id?: string;
}

export function StatsSection({ id }: StatsSectionProps) {
  return (
    <section
      id={id}
      className="relative"
      style={{
        // Subtle butter→cream gradient, different shade from
        // CateringTeaser's flat cream below, so the two adjacent
        // light sections register as distinct beats.
        background:
          "linear-gradient(180deg, #FBF3D0 0%, #FAF6F0 70%, #FAF6F0 100%)",
        padding: "clamp(80px, 10vw, 128px) 0 clamp(80px, 10vw, 120px)",
      }}
    >
      <div
        className="px-6 md:px-8"
        style={{ maxWidth: 1180, margin: "0 auto" }}
      >
        {/* ── Editorial frame, BeliefBelt's voice absorbed here ──
            The italic Newsreader line + DM Sans subordinate sit
            ABOVE the cards as the lyrical setup; the cards are the
            evidence beneath. One coherent section instead of two
            adjacent ones. */}
        <header
          className="text-center"
          style={{ maxWidth: 880, margin: "0 auto 56px" }}
        >
          {/* Coral leading rule, BeliefBelt's signature motif. */}
          <div
            aria-hidden="true"
            className="mx-auto mb-7"
            style={{
              width: 48,
              height: 2,
              background: "#C4564A",
              borderRadius: 999,
            }}
          />
          <p
            style={{
              fontFamily: displayFamily,
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "clamp(1.7rem, 3.4vw, 2.6rem)",
              lineHeight: 1.22,
              letterSpacing: "-0.005em",
              color: "#1D3461",
              margin: 0,
            }}
          >
            Australians say they want to be seen.
            <br />
            Just not to be put on stage.
          </p>
          <p
            style={{
              fontFamily: sansFamily,
              fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)",
              lineHeight: 1.6,
              color: "rgba(45,41,38,0.72)",
              margin: "20px auto 0",
              maxWidth: "44rem",
            }}
          >
            Most recognition tools were built for an audience. Lembra was
            built for the person, considered, on the day, and quietly
            handled, so it lands like someone remembered.
          </p>
        </header>

        {/* ── Three stat cards (white / butter / white) ──────────── */}
        <div className="grid gap-4 md:grid-cols-3 md:gap-5">
          {STATS.map((card) => (
            <StatCardView key={card.title} card={card} />
          ))}
        </div>
      </div>
    </section>
  );
}
